import React, { FC, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import useService from '../../hooks/service.hook';
import AnalyticsService from '../../services/analytics.service';
import TeamsService from '../../services/teams.service';
import UserService from '../../services/user.service';
import { AthleteAttendanceTeam } from '../../models/attendance.models';
import { StatsResponse } from '../../models/stats.models';
import { parseAttendanceResponse } from '../../components/team-attendance-breakdown/team-attendance-breakdown.helpers';
import {
  computeCurrentSeasonStart,
  getDefaultAnnualAnchor,
  getSeasonStartForYear,
  resolveSeasonFromAnchor,
  resolveSeasonRange,
  toApiDateRange,
} from '../../helpers/datetime.helpers';
import { formatSeasonYearLabel } from './training-log.helpers';

export interface TrainingLogContextValue {
  annualAnchor: Dayjs | null;
  seasonStart: Dayjs | null;
  seasonEnd: Dayjs | null;
  setSeasonStart: (date: Dayjs) => void;
  updateAnnualAnchor: (date: Dayjs) => Promise<void>;
  annualAnchorSaving: boolean;
  stats: StatsResponse | null;
  loading: boolean;
  error: boolean;
  refetch: () => void;
  seasonLabel: string;
  attendanceTeams: AthleteAttendanceTeam[] | null;
  attendanceLoading: boolean;
  hideAttendance: boolean;
  hideAnnualStartDate: boolean;
  onNavigateToTrainingLog?: (period: number, week: number) => void;
  onOpenAthleteTraining?: (trainingId: string | number) => void;
}

const TrainingLogContext = createContext<TrainingLogContextValue | null>(null);

export const useTrainingLogContext = (): TrainingLogContextValue => {
  const context = useContext(TrainingLogContext);
  if (!context) {
    throw new Error('useTrainingLogContext must be used within TrainingLogProvider');
  }
  return context;
};

export interface CoachViewConfig {
  teamId: string;
  memberId: string;
}

interface TrainingLogProviderProps {
  children: React.ReactNode;
  coachView?: CoachViewConfig;
  onNavigateToTrainingLog?: (period: number, week: number) => void;
  onOpenAthleteTraining?: (trainingId: string | number) => void;
}

export const TrainingLogProvider: FC<TrainingLogProviderProps> = ({
  children,
  coachView,
  onNavigateToTrainingLog,
  onOpenAthleteTraining,
}) => {
  const analyticsService = useService(AnalyticsService);
  const teamsService = useService(TeamsService);
  const userService = useService(UserService);
  const isCoachView = Boolean(coachView);
  // Extract stable primitives so object identity changes on re-render don't retrigger fetches
  const coachTeamId = coachView?.teamId ?? null;
  const coachMemberId = coachView?.memberId ?? null;
  const [annualAnchor, setAnnualAnchor] = useState<Dayjs | null>(null);
  const [seasonStart, setSeasonStart] = useState<Dayjs | null>(null);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [attendanceTeams, setAttendanceTeams] = useState<AthleteAttendanceTeam[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [error, setError] = useState(false);
  const [anchorLoading, setAnchorLoading] = useState(true);
  const [annualAnchorSaving, setAnnualAnchorSaving] = useState(false);

  useEffect(() => {
    analyticsService
      .getAnalyticsAnnualStartDate()
      .then((response) => {
        const annualStartDateStr = response.data.data?.annual_start_date;
        const anchor = annualStartDateStr ? dayjs(annualStartDateStr) : getDefaultAnnualAnchor();
        const { annualAnchor: resolvedAnchor, seasonStart: resolvedSeasonStart } = resolveSeasonFromAnchor(anchor);
        setAnnualAnchor(resolvedAnchor);
        setSeasonStart(resolvedSeasonStart);
        setAnchorLoading(false);
      })
      .catch(() => {
        const { annualAnchor: resolvedAnchor, seasonStart: resolvedSeasonStart } = resolveSeasonFromAnchor(
          getDefaultAnnualAnchor()
        );
        setAnnualAnchor(resolvedAnchor);
        setSeasonStart(resolvedSeasonStart);
        setAnchorLoading(false);
      });
  }, []);

  const handleSetSeasonStart = useCallback(
    (date: Dayjs) => {
      if (!annualAnchor) {
        setSeasonStart(date);
        return;
      }
      setSeasonStart(getSeasonStartForYear(annualAnchor, date.year()));
    },
    [annualAnchor]
  );

  const seasonRange = useMemo(() => {
    if (!annualAnchor || !seasonStart) {
      return null;
    }
    return resolveSeasonRange(annualAnchor, seasonStart);
  }, [annualAnchor, seasonStart]);

  const seasonEnd = seasonRange?.end ?? null;

  const fetchStats = useCallback(() => {
    if (!seasonRange) {
      return;
    }

    setLoading(true);
    setError(false);

    const { start, end } = toApiDateRange(seasonRange.start, seasonRange.end);

    const request =
      isCoachView && coachTeamId && coachMemberId
        ? teamsService.getAthleteTrainingLog(coachTeamId, coachMemberId, { start, end })
        : analyticsService.getStats(start, end);

    request
      .then((response) => {
        setStats(response.data.data);
        setLoading(false);
      })
      .catch(() => {
        setStats(null);
        setError(true);
        setLoading(false);
      });
  }, [analyticsService, teamsService, seasonRange, isCoachView, coachTeamId, coachMemberId]);

  const fetchAttendance = useCallback(() => {
    if (!seasonRange) {
      return;
    }

    setAttendanceLoading(true);

    const { start, end } = toApiDateRange(seasonRange.start, seasonRange.end);

    userService
      .getAthleteAttendance(start, end)
      .then((response) => {
        setAttendanceTeams(parseAttendanceResponse(response.data.data));
        setAttendanceLoading(false);
      })
      .catch(() => {
        setAttendanceTeams([]);
        setAttendanceLoading(false);
      });
  }, [userService, seasonRange]);

  const refetch = useCallback(() => {
    fetchStats();
    if (!isCoachView) {
      fetchAttendance();
    }
  }, [fetchStats, fetchAttendance, isCoachView]);

  const updateAnnualAnchor = useCallback(
    async (date: Dayjs) => {
      if (isCoachView) return;

      setAnnualAnchorSaving(true);
      const isoStringDate = date.format('YYYY-MM-DD');

      try {
        const response = await analyticsService.updateAnalyticsAnnualStartDate(isoStringDate);
        const updatedDate = response.data.data?.annual_start_date || isoStringDate;
        const resolvedAnchor = dayjs(updatedDate);
        setAnnualAnchor(resolvedAnchor);
        setSeasonStart((currentSeasonStart) =>
          currentSeasonStart
            ? getSeasonStartForYear(resolvedAnchor, currentSeasonStart.year())
            : computeCurrentSeasonStart(resolvedAnchor)
        );
      } finally {
        setAnnualAnchorSaving(false);
      }
    },
    [analyticsService, isCoachView]
  );

  useEffect(() => {
    if (!anchorLoading && seasonRange) {
      fetchStats();
      if (!isCoachView) {
        fetchAttendance();
      }
    }
  }, [anchorLoading, seasonRange, fetchStats, fetchAttendance, isCoachView]);

  const seasonLabel = seasonStart ? formatSeasonYearLabel(seasonStart) : '';

  const value = useMemo<TrainingLogContextValue>(
    () => ({
      annualAnchor,
      seasonStart,
      seasonEnd,
      setSeasonStart: handleSetSeasonStart,
      updateAnnualAnchor,
      annualAnchorSaving,
      stats,
      loading: loading || anchorLoading,
      error,
      refetch,
      seasonLabel,
      attendanceTeams: isCoachView ? null : attendanceTeams,
      attendanceLoading: isCoachView ? false : attendanceLoading || anchorLoading,
      hideAttendance: isCoachView,
      hideAnnualStartDate: isCoachView,
      onNavigateToTrainingLog,
      onOpenAthleteTraining,
    }),
    [
      annualAnchor,
      seasonStart,
      seasonEnd,
      stats,
      loading,
      anchorLoading,
      error,
      refetch,
      seasonLabel,
      handleSetSeasonStart,
      updateAnnualAnchor,
      annualAnchorSaving,
      attendanceTeams,
      attendanceLoading,
      isCoachView,
      onNavigateToTrainingLog,
      onOpenAthleteTraining,
    ]
  );

  return <TrainingLogContext.Provider value={value}>{children}</TrainingLogContext.Provider>;
};
